import React, { useEffect, useState } from "react";
import axiosInstance from "./axiosInstance";
import { getagentnoFromToken } from "../utils/auth";
import "./styles/box.css";

const AgentBoxes = () => {
  const agentNumber = getagentnoFromToken();
  const [boxes, setBoxes] = useState([]);
  const [orderPayments, setOrderPayments] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [expandedBox, setExpandedBox] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(null);

  useEffect(() => {
    fetchBoxes();
  }, []);

  const fetchBoxes = async () => {
    setError("");
    setLoading(true);
    setBoxes([]);
    setExpandedBox(null);
    setExpandedOrder(null);
  
    try {
      const response = await axiosInstance.get(`/agent/${agentNumber}/boxes`);
      setBoxes(response.data.boxes);
  
      // Fetch order details for all productIds
      response.data.boxes.forEach((box) => {
        box.items.forEach((item) => {
          fetchOrderPayments(item.productId);
        });
      });
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch boxes. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  

  const fetchOrderPayments = async (productId) => {
    if (!productId) return;
  
    console.log("Fetching order details for productId:", productId);
  
    try {
      const response = await axiosInstance.get(`/orders/findOrderByProductId/${productId}`);
  
      setOrderPayments((prevPayments) => ({
        ...prevPayments,
        [productId]: response.data, // Store response directly using productId as key
      }));
  
      console.log("Order details received:", response.data);
    } catch (error) {
      console.error("Failed to fetch order payment details:", error);
    }
  };
  
  

  const toggleBoxDetails = (boxId) => {
    setExpandedBox((prevBoxId) => (prevBoxId === boxId ? null : boxId));
  };

  const toggleOrderDetails = (orderNumber) => {
    setExpandedOrder((prevOrder) => {
      const newOrder = prevOrder === orderNumber ? null : orderNumber;
  
      if (newOrder && !orderPayments[newOrder]) {
        fetchOrderPayments(newOrder); // Fetch order payment details only if not already fetched
      }
  
      return newOrder;
    });
  };
  

  const extractDestination = (destinationString) => {
    try {
      const validJsonString = destinationString
        .replace(/(\w+):/g, '"$1":')
        .replace(/'/g, '"');

      const destinationObj = JSON.parse(validJsonString);
      const { county, town, area, specific } = destinationObj;

      return { county, town, area, specific };
    } catch (error) {
      console.error("Error parsing destination string:", error);
      return { county: null, town: null, area: null, specific: null };
    }
  };

  const categorizeBoxes = (boxes) => {
    const locallyDelivered = [];
    const toBePickedUp = [];
    const toHub = [];

    boxes.forEach((box) => {
      const { county, town, area, specific } = extractDestination(box.destination);
      const currentParts = box.currentplace.split(", ");

      if (currentParts[0] === county && currentParts[1] === town && currentParts[2] === area) {
        if (currentParts[3] === specific) {
          toBePickedUp.push(box);
        } else {
          locallyDelivered.push(box);
        }
      } else {
        toHub.push(box);
      }
    });

    return { locallyDelivered, toBePickedUp, toHub };
  };

  const { locallyDelivered, toBePickedUp, toHub } = categorizeBoxes(boxes);

  const renderBoxSection = (title, boxList, showFullDetails = false) => (
    <>
      {boxList.length > 0 && (
        <div className="box-section">
          <h3 className="box-section-title">{title}</h3>
          <ul className="box-list-items">
            {boxList.map((box) => (
              <li key={box._id} className="box-item">
                <div>
                  <strong>Box Number:</strong> {box.boxNumber} <br />
                  <strong>Box ID:</strong> {box.boxid} <br />
                  <strong>Destination:</strong> {box.destination}<br />
                  <strong>Current Place:</strong> {box.currentplace} <br />
                  <strong>Packing Date:</strong> {new Date(box.packingDate).toLocaleString()} <br />
                  <strong>
                    Items:{" "}
                    <span className="item-toggle" onClick={() => toggleBoxDetails(box._id)}>
                      {box.items.length} orders (click to view)
                    </span>
                  </strong>
                </div>

                {expandedBox === box._id && (
                  <div className="item-details">
                    <h4>Item Details:</h4>
                    <ul>
                      {box.items.map((item, index) => {
                        const orderInfo = orderPayments[item.productId] || {};

                        return (
                          <li key={index} className="item-detail">
                            {showFullDetails ? (
                              <>
                                <span
                                  className="order-toggle"
                                  onClick={() => toggleOrderDetails(item.orderNumber)}
                                >
                                  <strong>Order Number:</strong> {item.orderNumber} (click to view)
                                </span>
                                {expandedOrder === item.orderNumber && (
                                  <div className={`order-details ${orderPayments[item.orderNumber]?.paid ? "paid" : "unpaid"}`}>
                                    <strong>order Number:</strong> {orderPayments[item.orderNumber]?.orderNumber || "Order not found"} <br />
                                    <strong>Username:</strong> {orderPayments[item.orderNumber]?.username || "Order not found"} <br />
                                    <strong>Amount:</strong> {orderPayments[item.orderNumber]?.totalPrice ? `KES: ${orderPayments[item.orderNumber]?.totalPrice}` : "Order not found"} <br />
                                    <strong>Status:</strong> {orderPayments[item.orderNumber]?.paid ? "Paid ✅" : "Unpaid ❌"}
                                  </div>
                                )}
                              </>
                            ) : (
                              <strong>Order ID: {item.orderNumber}</strong> 
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );

  return (
    <div className="agent-boxes-container">
      <h2 className="agent-boxes-title">Agent Boxes</h2>

      {error && <p className="error-message">{error}</p>}
      {loading && <p className="loading-message">Loading...</p>}

      {!loading && !error && boxes.length === 0 && (
        <p className="no-data-message">No boxes available for this agent.</p>
      )}


      {renderBoxSection("To Be Picked Up", toBePickedUp, true)}
      {renderBoxSection("Locally Delivered", locallyDelivered, false)}
      {renderBoxSection("To Hub", toHub, false)}
    </div>
  );
};

export default AgentBoxes;
